import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';
import { CHIP_LOOK, valueLabelKey } from '../MetadataSuggestionPanel';
import type { LoopStats } from '../../../utils/recipeMetadataScoring';
import { REVIEW_FIELDS, type FieldState } from '../../../types/recipeMetadata';

/**
 * The loop, made visible: how often the AI was right against your answers,
 * per field, over the last 30 days — the same scoring as
 * `npm run eval:recipe-metadata` (src/utils/recipeMetadataScoring.ts) — and
 * the mistake it makes most. Glance confirms are shown apart: a quick look at
 * sure answers says less than a row answered one by one. A time counts as
 * right within 5 minutes or 15% (timesAgree), and the typical miss is shown
 * beside it.
 */
export const ReviewLoopStrip: React.FC<{ stats: LoopStats | null }> = ({ stats }) => {
  const { t } = useTranslation();
  if (!stats) return null;
  const pct = (x: number) => Math.round(x * 100);
  const top = stats.topCorrection;
  return (
    <View style={styles.strip} testID="review-loop-strip">
      <View style={styles.titleBlock}>
        <Text style={styles.title}>{t('importReview.loop.title')}</Text>
        <Text style={styles.meta}>
          {t('importReview.loop.meta', { version: stats.promptVersion ?? '—', days: stats.days })}
        </Text>
      </View>
      {REVIEW_FIELDS.map((field) => {
        const { single, glance } = stats.fields[field];
        return (
          <View key={field} style={styles.field} testID={`review-loop-${field}`}>
            <Text style={styles.fieldValue}>
              {single.n > 0
                ? t('importReview.loop.field', { field: t(`recipeMetadata.${field}`), percent: pct(single.accuracy) })
                : `${t(`recipeMetadata.${field}`)} · ${t('importReview.loop.noAnswers')}`}
            </Text>
            {glance.n > 0 ? (
              <Text style={styles.meta}>{t('importReview.loop.glance', { percent: pct(glance.accuracy) })}</Text>
            ) : null}
            {field === 'time' && stats.timeMedianError != null ? (
              <Text style={styles.meta}>{t('importReview.loop.timeError', { minutes: Math.round(stats.timeMedianError) })}</Text>
            ) : null}
          </View>
        );
      })}
      {top ? (
        <Text style={styles.meta}>
          {t('importReview.loop.mostCorrected', {
            field: t(`recipeMetadata.${top.field}`),
            from: t(valueLabelKey(top.field, top.from)),
            to: t(valueLabelKey(top.field, top.to)),
            count: top.count,
          })}
        </Text>
      ) : null}
    </View>
  );
};

const LEGEND: FieldState[] = ['confirmed', 'sure', 'unsure', 'missing', 'auto'];
/** A sample of what each chip carries, so the legend reads like the chips do. */
const SAMPLE: Record<FieldState, string> = { confirmed: '✓', sure: '92%', unsure: '? 60%', missing: '—', auto: 'auto' };

/** What each chip look means. Drawn with the chips' own looks (CHIP_LOOK). */
export const ReviewLegend: React.FC = () => {
  const { t } = useTranslation();
  return (
    <View style={styles.legend}>
      {LEGEND.map((state) => (
        <View key={state} style={styles.legendItem}>
          <View style={[styles.swatch, CHIP_LOOK[state].chip]}>
            <Text style={[styles.swatchText, { color: CHIP_LOOK[state].text }]}>{SAMPLE[state]}</Text>
          </View>
          <Text style={styles.legendText}>{t(`importReview.legend.${state}`)}</Text>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
    marginHorizontal: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 12,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.aiTint,
  },
  titleBlock: {
    gap: 2,
  },
  title: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.ai,
  },
  field: {
    gap: 1,
  },
  fieldValue: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.ai,
  },
  meta: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.sm,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  swatch: {
    minWidth: 28,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 6,
    alignItems: 'center',
  },
  swatchText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    lineHeight: 14,
  },
  legendText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },
});
